// Attendee Identity Service
// Uses Firebase Anonymous Auth for server-validated identity.
// The auth UID serves as the attendee's `oderId` across all Firestore writes.
// Supports cross-device identity linking via code words (Firestore-backed).

import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, auth, ensureAnonymousAuth } from './firebase';

const ATTENDEE_NICKNAME_KEY = 'votebeats_attendee_nickname';
const ANON_DISMISSED_KEY = 'votebeats_anon_dismissed';
const CODE_WORD_HASH_KEY = 'votebeats_code_word_hash';

// Legacy key — kept for migration only
const LEGACY_ATTENDEE_ID_KEY = 'votebeats_attendee_id';

// Get attendee ID (Firebase anonymous auth UID).
// This is async because it may need to sign in anonymously on first call.
export async function getAttendeeIdAsync(): Promise<string> {
  return ensureAnonymousAuth();
}

// Synchronous getter — returns the cached UID if auth is already resolved.
// Falls back to localStorage legacy ID if auth hasn't completed yet.
// Prefer getAttendeeIdAsync() in all new code.
export function getAttendeeId(): string {
  if (auth.currentUser) {
    return auth.currentUser.uid;
  }
  // Fallback: return legacy localStorage ID (for brief window before auth resolves)
  return localStorage.getItem(LEGACY_ATTENDEE_ID_KEY) || 'pending-auth';
}

// Get attendee nickname (optional)
export function getAttendeeNickname(): string | null {
  return localStorage.getItem(ATTENDEE_NICKNAME_KEY);
}

// Set attendee nickname
export function setAttendeeNickname(nickname: string): void {
  localStorage.setItem(ATTENDEE_NICKNAME_KEY, nickname);
  if (nickname.trim().length >= 2) {
    localStorage.removeItem(ANON_DISMISSED_KEY);
  }
}

// Get anonymous dismiss state
export function getAnonDismissed(): boolean {
  return localStorage.getItem(ANON_DISMISSED_KEY) === 'true';
}

// Set anonymous dismiss state
export function setAnonDismissed(dismissed: boolean): void {
  if (dismissed) {
    localStorage.setItem(ANON_DISMISSED_KEY, 'true');
  } else {
    localStorage.removeItem(ANON_DISMISSED_KEY);
  }
}

// Clear attendee nickname
export function clearAttendeeNickname(): void {
  localStorage.removeItem(ATTENDEE_NICKNAME_KEY);
}

// Clear attendee identity (will create new on next sign-in)
export function clearAttendeeIdentity(): void {
  localStorage.removeItem(LEGACY_ATTENDEE_ID_KEY);
  localStorage.removeItem(ATTENDEE_NICKNAME_KEY);
}

// Get storage keys for external cleanup
export function getAttendeeStorageKeys(): { id: string; nickname: string } {
  return {
    id: LEGACY_ATTENDEE_ID_KEY,
    nickname: ATTENDEE_NICKNAME_KEY,
  };
}

// SHA-256 hash for code words (browser-native crypto)
async function sha256Hash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Set a code word for the current attendee (Firestore-backed for cross-device support)
export async function setCodeWord(codeWord: string): Promise<{ success: boolean; message: string }> {
  if (!codeWord || codeWord.trim().length < 4) {
    return { success: false, message: 'Code word must be at least 4 characters' };
  }

  const normalizedCodeWord = codeWord.trim().toUpperCase();
  const hash = await sha256Hash(normalizedCodeWord);
  const currentAttendeeId = await getAttendeeIdAsync();

  // Check Firestore registry
  const registryRef = doc(db, 'codeWordRegistry', hash);
  const snap = await getDoc(registryRef);

  if (snap.exists() && snap.data().attendeeId !== currentAttendeeId) {
    return { success: false, message: 'This code word is already in use by another identity' };
  }

  // Register in Firestore
  await setDoc(registryRef, { attendeeId: currentAttendeeId, createdAt: new Date().toISOString() });

  // Store the hash locally
  localStorage.setItem(CODE_WORD_HASH_KEY, hash);

  return { success: true, message: 'Code word set successfully' };
}

// Link identity using a code word (Firestore-backed for cross-device access)
export async function linkWithCodeWord(codeWord: string): Promise<{
  success: boolean;
  message: string;
  linkedAttendeeId?: string;
  previousAttendeeId?: string;
}> {
  if (!codeWord || codeWord.trim().length < 4) {
    return { success: false, message: 'Code word must be at least 4 characters' };
  }

  const normalizedCodeWord = codeWord.trim().toUpperCase();
  const hash = await sha256Hash(normalizedCodeWord);
  const currentAttendeeId = await getAttendeeIdAsync();

  // Look up in Firestore
  const registryRef = doc(db, 'codeWordRegistry', hash);
  const snap = await getDoc(registryRef);

  if (!snap.exists()) {
    return { success: false, message: 'Code word not found. Set it on your original device first.' };
  }

  const linkedAttendeeId = snap.data().attendeeId;

  // If already linked to this ID, nothing to do
  if (linkedAttendeeId === currentAttendeeId) {
    return { success: true, message: 'Already linked to this identity', linkedAttendeeId };
  }

  // Store previous ID
  const previousAttendeeId = currentAttendeeId;

  // Note: With Firebase Auth, cross-device linking would require custom token minting
  // via a Cloud Function. For now, we store the link reference for data association.
  localStorage.setItem(CODE_WORD_HASH_KEY, hash);

  return {
    success: true,
    message: 'Identity linked successfully! Your history is now synced.',
    linkedAttendeeId,
    previousAttendeeId
  };
}

// Get current code word hash (if set)
export function getCodeWordHash(): string | null {
  return localStorage.getItem(CODE_WORD_HASH_KEY);
}

// Check if attendee has a code word set
export function hasCodeWord(): boolean {
  return !!localStorage.getItem(CODE_WORD_HASH_KEY);
}

// Clear code word (unlink from cross-device identity)
export async function clearCodeWord(): Promise<void> {
  const hash = localStorage.getItem(CODE_WORD_HASH_KEY);
  if (hash) {
    // Remove from Firestore registry
    const registryRef = doc(db, 'codeWordRegistry', hash);
    await deleteDoc(registryRef);
  }
  localStorage.removeItem(CODE_WORD_HASH_KEY);
}
